import React, {useState} from "react";
import {observer} from "mobx-react";
import {useStores} from "../../Hooks";
import GeneralDialog from "./GeneralDialog";
import {sitePrepUserTypeEnum} from "../../serverClientShared/sharedEnums";
import ConditionalComponent from "../Shared/ConditionalComponent";
import {IconButton, TextField} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import MenuItem from "@mui/material/MenuItem";
import DynamicAvatar from "../Shared/DynamicAvatar";
import DoneIcon from "@mui/icons-material/Done";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SearchIcon from "@mui/icons-material/Search";
import Utility from "../../Utility";
import {MembersRequester} from "../Domain/Groups/MembersRequester";

let searchStrs = [];

const toDisplayFields = (user) => {
    const firstName = user.FirstName || user.Name;
    return {
        firstName,
        fullName: [firstName, user.LastName].filter(Boolean).join(' '),
        avatarUser: {Name: firstName, LastName: user.LastName},
    };
};

const AddSitePrepUsersModal = observer(() => {
    const {modalStore, generalStore} = useStores();

    const [selectedEditors, setSelectedEditors] = useState(new Map());
    const [selectedViewers, setSelectedViewers] = useState(new Map());
    const [selectionTab, setSelectionTab] = useState(0);
    const [filteredUsersList, setFilteredUsersList] = useState([]);
    const [usersSearchVal, setUsersSearchVal] = useState('');

    const isAddNew = !!modalStore.modalData.isAddNew;

    const searchUsers = event => {
        const search = event.target.value;
        const searchTrimmed = search.trim();
        setUsersSearchVal(search);
        if (searchTrimmed.length < 3) {
            setFilteredUsersList([]);
            return;
        }

        searchStrs.push(searchTrimmed);
        setTimeout(async () => {
            const lastSearchStr = searchStrs[searchStrs.length - 1];
            searchStrs = [];
            if (!lastSearchStr) {
                return;
            }
            const result = await MembersRequester.getMembersForSelect(generalStore.workspace, null, 0, 50, lastSearchStr);
            const members = result?.data?.data || [];
            setFilteredUsersList(members.filter(m => !m.IsInvite));
        }, 1000);

    }

    const setSelectedUser = (user, selectedUsers) => {
        const setSelectedUsers = selectionTab === 0 ? setSelectedEditors : setSelectedViewers;
        const setOtherSelectedUsers = selectionTab === 0 ? setSelectedViewers : setSelectedEditors;
        const otherSelectedUsers = selectionTab === 0 ? selectedViewers : selectedEditors;
        const userId = user._id;
        if (selectedUsers.has(userId)) {
            selectedUsers.delete(userId);
        } else {
            selectedUsers.set(userId, {
                UserRef: user,
                UserType: selectionTab === 0 ? sitePrepUserTypeEnum.EDITOR : sitePrepUserTypeEnum.VIEWER,
            });
            otherSelectedUsers.delete(userId);
            setOtherSelectedUsers(new Map(otherSelectedUsers));
        }
        setSelectedUsers(new Map(selectedUsers));
    }

    const renderBody = () => {
        const selectedUsers = selectionTab === 0 ? selectedEditors : selectedViewers;

        return (
            <div className='add-site-prep-users-container'>
                <div className='switch-tab-container'>
                    <div className={`switch-tab${selectionTab === 0 ? ' selected' : ''}`} onClick={() => setSelectionTab(0)}>
                        {generalStore.translate('siteEditors')}
                    </div>
                    <div className={`switch-tab${selectionTab === 1 ? ' selected' : ''}`} onClick={() => setSelectionTab(1)}>
                        {generalStore.translate('siteViewers')}
                    </div>
                </div>

                <div className='search-users-container'>

                    <TextField
                        className='search-users'
                        placeholder={generalStore.translate('searchEmailOrName')}
                        value={usersSearchVal}
                        onChange={searchUsers}
                        fullWidth
                        variant='outlined'
                    />
                    <IconButton className='search-users-reset' onClick={() => {
                        setUsersSearchVal('');
                        setFilteredUsersList([]);
                    }}>
                        {usersSearchVal.length < 3 ? <SearchIcon/> : <CloseIcon/>}
                    </IconButton>
                </div>

                <ConditionalComponent if={!!filteredUsersList.length}>
                    <div className='users-list special-scroll'>
                        {filteredUsersList.map(user => {
                            const {fullName, avatarUser} = toDisplayFields(user);
                            return (
                                <MenuItem key={user._id}
                                          className={'user-list-item' + (selectedUsers.has(user._id) ? ' disabled' : '')}
                                          onClick={() => setSelectedUser(user, selectedUsers)}>
                                    <DynamicAvatar user={avatarUser} imageSrc={user.UserIcon}/>
                                    <div className='user-name-email-container'>
                                        <div className='user-name-container'>{fullName}</div>
                                        <div className='user-email-container'>{user.Email}</div>
                                    </div>
                                    {selectedUsers.has(user._id) ? <DoneIcon/> : null}
                                </MenuItem>
                            );
                        })}
                    </div>
                </ConditionalComponent>

                <ConditionalComponent if={!filteredUsersList.length}>
                    <div className='selected-users-container special-scroll'>
                        {[...selectedUsers.values()].map(userObj => {
                            const user = userObj.UserRef || {};
                            const {fullName, avatarUser} = toDisplayFields(user);
                            return (
                                <div key={user._id} className='selected-user'>
                                    <DynamicAvatar user={avatarUser} imageSrc={user.UserIcon}/>
                                    <div className='selected-user-item'>{user.Email}</div>
                                    <div className='selected-user-item'>{fullName}</div>
                                    <DeleteOutlineIcon className="remove" onClick={() => setSelectedUser(user, selectedUsers)}/>
                                </div>
                            )
                        })}
                    </div>
                </ConditionalComponent>
            </div>
        )
    }

    const addSelfAsEditor = (editors, viewers) => {
        const currentUserId = generalStore.userDetails._id;

        const updatedSelectedEditors = new Map(editors);
        updatedSelectedEditors.set(currentUserId, {
            UserRef: generalStore.userDetails,
            UserType: sitePrepUserTypeEnum.EDITOR
        });
        const updatedSelectedViewers = new Map(viewers);
        updatedSelectedViewers.delete(currentUserId);
        return {updatedSelectedEditors, updatedSelectedViewers}
    }

    const onConfirm = async () => {
        modalStore.modalData.sitePrepData.SitePrepAssignees = [...selectedEditors.values(), ...selectedViewers.values()];
        modalStore.isAddSitePrepUsersModalOpen = false;
        if (isAddNew) {
            // modalStore.isAddSitePrepFloorPlanModalOpen = true;   //TODO implement when adding floor plan pdf implemented
            if (!selectedEditors.has(generalStore.userDetails._id)) {
                const {updatedSelectedEditors, updatedSelectedViewers} = addSelfAsEditor(selectedEditors, selectedViewers);
                modalStore.modalData.sitePrepData.SitePrepAssignees = [...updatedSelectedEditors.values(), ...updatedSelectedViewers.values()];
            }
            await modalStore.modalData.onFinishAddSitePrep();
            Utility.sleep(100).then(() => modalStore.resetModalData());
        } else {
            await modalStore.modalData.updateSitePrep();
        }
    }

    const onEnter = () => {
        setUsersSearchVal('');
        setFilteredUsersList([]);
        const editors = new Map();
        const viewers = new Map();

        if (!!modalStore.modalData.sitePrepData?.SitePrepAssignees?.length) {
            modalStore.modalData.sitePrepData.SitePrepAssignees.forEach(user => {
                const userId = user.UserRef?._id || user.UserRef;    //if UserRef has not been populated
                if (!user.UserRef?._id) {
                    if (userId === generalStore.userDetails._id) {
                        user.UserRef = generalStore.userDetails;
                    } else user.UserRef = {_id: userId};
                }
                if (user.UserType === sitePrepUserTypeEnum.EDITOR) {
                    editors.set(userId, user);
                } else viewers.set(userId, user);
            });
        }

        if (isAddNew && !editors.has(generalStore.userDetails._id)) {
            const {updatedSelectedEditors, updatedSelectedViewers} = addSelfAsEditor(editors, viewers);
            setSelectedEditors(updatedSelectedEditors);
            setSelectedViewers(updatedSelectedViewers);
        } else {
            setSelectedEditors(editors);
            setSelectedViewers(viewers);
        }

        setSelectionTab(0);
    }

    return (
        <GeneralDialog
            openControl='isAddSitePrepUsersModalOpen'
            className={'procedure-option-dialog site-prep-options users-dialog'}
            onEnter={onEnter}
            title={generalStore.translate('addPeople')}
            body={renderBody()}
            noCancel={isAddNew}
            isConfirmDisabled={false}
            confirmText={generalStore.translate(isAddNew ? 'next' : 'update')}
            onConfirm={onConfirm}
            extraClose
            goBackControl={isAddNew ? 'isSelectSitePrepTemplateModalOpen' : ''}
        />
    );
});

export default AddSitePrepUsersModal;
